// Service autonome de la case Commande x MS (PRESTATAIRE).
// Cible les vraies routes prestataire confirmees cote core api.php :
//   - GET  /subsequents/commande/{id}                       (chargement : SubsequentController@showById)
//   - POST /subsequents/{id}/candidater | /refuser          (actions, remplies a l'etape Candidature)
//   - /subsequents/prestataire/signature/{id}/{signataire-info,demander-otp,verifier-otp,candidater}
//                                                            (signature OTP presta : PrestataireSignatureController)
// Aucun sniffing d'URL : l'id est passe en argument (le legacy Candidature.tsx derivait l'id de
// window.location.pathname.split('/').pop() — smell a NE PAS reproduire).
//
// NB : la signature presta vise /subsequents/PRESTATAIRE/signature/* (PrestataireSignatureController),
// PAS /subsequents/signature/* qui est la signature ACHETEUR (MsSignatureController). Cf.
// frontend/documentation/04-audits/audit-commande-ms.md.
//
// NB : le service partage commandeService (entityService.tsx) n'est PAS reutilise ici — il vise
// l'endpoint legacy /commande-ms/{id}, pas /subsequents/commande/{id}. On reste sur un raw fetch
// autonome, fidele a l'oracle MS (SuiviDeCommande.tsx:287). Ne pas "corriger" en reutilisant
// commandeService : ca casserait le chargement.
import { CommandeMS } from '../types';

const authedFetch = async <T = unknown>(path: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");

  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.json().catch(() => ({})) as Promise<T>;
};

// Enveloppe de reponse de l'endpoint : { status: 'success' | 'error', data: <commande> }.
// `status` ici est le statut HTTP-applicatif (chaine), a ne pas confondre avec CommandeMS.status
// (StatusRef du cycle de vie) — d'ou un type d'enveloppe dedie plutot qu'une intersection.
interface CommandeMsEnvelope {
  status?: string;
  data?: CommandeMS;
}

// Charge la commande MS prestataire : GET /subsequents/commande/{id}. Id passe en argument,
// jamais devine depuis l'URL. Reproduit le garde-fou de l'oracle (SuiviDeCommande.tsx:301-303) :
// un 200 a corps d'erreur ({status:'error'}) ou sans `data` doit LEVER, pour que le shell affiche
// l'etat .scms__error au lieu de retomber silencieusement sur un objet vide/brut.
export const getCommandeMs = async (commandeId: number | string): Promise<CommandeMS> => {
  const json = await authedFetch<CommandeMsEnvelope>(`/subsequents/commande/${commandeId}`);
  if (json.status !== undefined && json.status !== 'success') {
    throw new Error('Format de réponse API inattendu');
  }
  if (!json.data) {
    throw new Error('Réponse API sans données de commande');
  }
  return json.data;
};

const apiBase = (): string => process.env.REACT_APP_API_URL ?? '';

// --- ACTIONS prestataire (onglet Candidature) ---------------------------------
// Toutes id-based : la commande passe son id en argument, JAMAIS via window.location (le legacy
// Candidature.tsx derivait l'id de window.location.pathname.split('/').pop() : smell evite).

// Soumet la candidature (mode automatique) : POST /subsequents/{id}/candidater.
export const candidaterCommande = (
  commandeId: number | string,
  payload: unknown,
): Promise<unknown> =>
  authedFetch(`/subsequents/${commandeId}/candidater`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// Refuse la consultation : POST /subsequents/{id}/refuser.
export const refuserConsultation = (commandeId: number | string): Promise<unknown> =>
  authedFetch(`/subsequents/${commandeId}/refuser`, { method: 'POST' });

// Signale au back l'attestation "documents administratifs a jour" : route DEDIEE (compte-rendu
// back), PUT /subsequents/{id}/attestation-documents, body { atteste }. atteste=true horodate
// attestation_documents_at = now() (la valeur EST la preuve datee) ; false remet null.
// POURQUOI une route dediee et pas un flag dans le body du candidater : la garde back du candidater
// LIT attestation_documents_at et refuse (422) tant qu'elle est null. On persiste donc l'attestation
// AVANT de declencher le depot / la signature, aussi bien en mode automatique qu'en mode manuscrit
// (ou le POST final .../candidater part du widget SignatureFlow, hors de notre controle temporel).
// Id en argument, jamais window.location.
export const updateAttestationDocuments = (
  commandeId: number | string,
  atteste: boolean,
): Promise<unknown> =>
  authedFetch(`/subsequents/${commandeId}/attestation-documents`, {
    method: 'PUT',
    body: JSON.stringify({ atteste }),
  });

// Enregistre la candidature en brouillon (sans soumettre) : POST /subsequents/commande/{id}/save.
export const saveBrouillonCandidature = (
  commandeId: number | string,
  payload: unknown,
): Promise<unknown> =>
  authedFetch(`/subsequents/commande/${commandeId}/save`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// URL de signature presta (mode manuscrite), passee a SignatureFlow via la prop `signerUrl` : elle
// REMPLACE le POST final (.../signer) par l'endpoint metier .../candidater. SignatureFlow construit
// son baseUrl /subsequents/prestataire/signature/{id} a partir de resourcePrefix="subsequents/prestataire".
// Route core confirmee (api.php:768, PrestataireSignatureController@signerEtCandidater).
export const buildCandidaterSignerUrl = (commandeId: number | string): string =>
  `${apiBase()}/subsequents/prestataire/signature/${commandeId}/candidater`;

// Mode de signature MS PRESTATAIRE : GET /app/signature-mode -> { ms_prestataire_mode }.
// CORRECTION d'un bug latent de l'oracle : Candidature.tsx:178 lisait `ms_mode` (= mode de signature
// ACHETEUR), alors que AppController@signatureMode expose un champ dedie `ms_prestataire_mode`
// (core AppController.php:21) pour le prestataire. On lit le bon champ -> le presta suit SA config.
// Endpoint public (fetch sans auth) -> plain fetch (pas authedFetch qui exige un token).
export const getMsSignatureMode = async (): Promise<'manuscrite' | 'automatique'> => {
  try {
    const response = await fetch(`${apiBase()}/app/signature-mode`);
    if (!response.ok) return 'automatique';
    const data = (await response.json()) as { ms_prestataire_mode?: string };
    return data.ms_prestataire_mode === 'manuscrite' ? 'manuscrite' : 'automatique';
  } catch {
    return 'automatique';
  }
};

// --- Messagerie : compteurs de non-lus (badges Q&R / Messagerie) ---------------
export interface UnreadCountsApi {
  questions_reponses: number;
  messagerie: number;
}

// Compteurs de non-lus par canal, cote PRESTATAIRE : GET /subsequents/messages/unread-counts
// ?operation_ids[]={opId}&auteur_type=prestataire (le legacy presta utilisait auteur_type=prestataire,
// cf. SuiviDeCommande.tsx:221). operationId = id de l'OPERATION (la messagerie est par operation).
export const getUnreadCounts = async (
  operationId: number | string,
): Promise<UnreadCountsApi | null> => {
  const json = await authedFetch<{ data?: Record<string, UnreadCountsApi> }>(
    `/subsequents/messages/unread-counts?operation_ids[]=${operationId}&auteur_type=prestataire`,
  );
  return json.data?.[String(operationId)] ?? null;
};

// --- Marché : signature bilaterale + PDF (onglet Notifications et signatures) -----------------
// Variante blob d'authedFetch (reponse PDF), pour le telechargement du marche signe.
const authedFetchBlob = async (path: string, options: RequestInit = {}): Promise<Blob> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error("Aucun token d'authentification trouvé");
  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(data.message || `Erreur HTTP: ${response.status}`);
  }
  return response.blob();
};

// Signature du marche cote PRESTATAIRE (mode automatique) : POST /subsequents/private/sign/.
// (Cote acheteur c'est /subsequents/public/sign/ ; ne pas confondre.) Ids en argument.
export const signMarchePrivate = (
  operationId: number | string,
  commandeId: number | string,
): Promise<unknown> =>
  authedFetch('/subsequents/private/sign/', {
    method: 'POST',
    body: JSON.stringify({ operation_id: operationId, commande_id: commandeId }),
  });

export interface SignedNamesResponse {
  acheteur?: { signataire_nom?: string };
  prestataire?: { signataire_nom?: string };
}

// Noms reellement utilises a la signature : GET /subsequents/operation/{opId}/signed-names?commande_id=.
export const getSignedNames = (
  operationId: number | string,
  commandeId?: number | string,
): Promise<SignedNamesResponse> => {
  const params = commandeId ? `?commande_id=${commandeId}` : '';
  return authedFetch<SignedNamesResponse>(
    `/subsequents/operation/${operationId}/signed-names${params}`,
  );
};

// Declenche le telechargement d'un Blob cote navigateur (remplace les <a download> inline du legacy).
export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Recupere le PDF du marche subsequent signe : POST /documents/signed-contrat (source MS).
// external_id = NUMERO de l'operation (cf. oracle Marche.tsx:738,754). Consomme par la
// previsualisation (iframe) ET par le telechargement -> une seule source d'appel.
export const fetchSignedMarcheBlob = (numero: string | number): Promise<Blob> =>
  authedFetchBlob('/documents/signed-contrat', {
    method: 'POST',
    body: JSON.stringify({ external_id: String(numero), external_type: 'operation', source: 'MS' }),
  });

// Telecharge le marche subsequent signe (PDF).
export const downloadSignedMarche = async (
  numero: string | number,
  fileName: string,
): Promise<void> => {
  const blob = await fetchSignedMarcheBlob(numero);
  triggerBlobDownload(blob, fileName);
};

// On expose authedFetch pour d'eventuelles actions ultérieures, toutes id-based.
export { authedFetch };
